<script lang="ts">
/** DEV: 이미 낸 묶음 이유 경고(같은 글은 한 번만 — 형제 버튼마다 같은 경고가 나지 않게) */
const warnedReasons = new Set<string>();
/** DEV: 이미 낸 긴 title 경고(같은 글은 한 번만) */
const warnedTitles = new Set<string>();

/** 막힌 버튼을 눌렀을 때 이유를 읽어 주는 polite live 구역(JR90-18) — 호스트(가까운 `role="dialog"`, 없으면 body)마다 하나 */
const liveRegions = new WeakMap<HTMLElement, HTMLElement>();
/** 같은 문장 연속 알림 간격(ms) — 이 안의 같은 글은 한 번만 */
const LIVE_REPEAT_MS = 1500;
let lastAnnounced = { text: "", at: 0 };

/**
 * 막힌 이유를 polite live 로 한 번 알린다. `aria-modal` 모달 밖(body)의 live 구역은 화면 읽기가 숨은 것으로 다뤄 읽지 않으므로 버튼이 든 모달 **안**에 둔다.
 * 같은 글을 다시 알리려면 구역을 비웠다 채워야 해서 한 틱 뒤에 쓴다.
 */
function announceReason(text: string, from: HTMLElement | null): void {
  const now = Date.now();
  if (text === lastAnnounced.text && now - lastAnnounced.at < LIVE_REPEAT_MS)
    return;
  lastAnnounced = { text, at: now };
  const host = from?.closest<HTMLElement>('[role="dialog"]') ?? document.body;
  let region = liveRegions.get(host);
  if (!region?.isConnected) {
    region = document.createElement("div");
    region.className = "ui-sr-only";
    region.setAttribute("role", "status");
    region.setAttribute("aria-live", "polite");
    region.setAttribute("aria-atomic", "true");
    host.append(region);
    liveRegions.set(host, region);
  }
  const target = region;
  target.textContent = "";
  window.setTimeout(() => {
    target.textContent = text;
  }, 50);
}
</script>

<script setup lang="ts">
import type { ReasonPlace } from "@/components/reasonContext";
import type { UiIconName } from "@/components/UiIcon.vue";
import type { FunctionalComponent } from "vue";

import {
  computed,
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  onUpdated,
  ref,
  useAttrs,
  useId,
  useTemplateRef,
  watch,
} from "vue";

import InfoTip from "@/components/InfoTip.vue";
import {
  REASON_CONTEXT,
  REASON_PAGE,
  usePageBlockedByRole,
} from "@/components/reasonContext";
import { scheduleRowRepeatCheck } from "@/components/rowRepeat";
import UiIcon from "@/components/UiIcon.vue";

/**
 * 처리 중 상태를 가진 버튼(`.btn`). 문구 교체형 로딩("~중…") 123곳의 잠금 · 접근성 처리를 한 가지로 맞춘다.
 * - 처리 중 · 잠김 · 막힘은 `disabled` 가 아니라 `aria-disabled="true"` 로 둔다. `disabled` 로 바꾸면 브라우저가 포커스를 빼앗아
 *   키보드 사용자가 위치를 잃는다(대시보드 「새로 고침」 방식). 그동안 누름은 무시하고 `click` 을 보내지 않는다(submit 도 막는다).
 * - 처리 중에는 `aria-busy="true"` + 14px 회전 표지(동작 줄이기면 멈춘 점). 문구가 짧아져도 폭이 줄지 않게 누르기 직전 폭을 min-width 로 잡는다.
 *   꼬리표: `progressText`(「(3/10)」), `showElapsed` 면 10초부터 「· 12초」(AI 생성 최대 30초).
 * - 막힌 이유(`blockedReason`)는 title 툴팁에만 두지 않고 보이는 한 줄 보조 글자로 둔다(UXB-07). 버튼은 aria-describedby 로 잇는다.
 *   표 칸처럼 공간이 없을 때만 `reasonPlacement="sr"`(화면 읽기용 숨은 글자 + title).
 * - 이유를 보일 때만 버튼과 이유 글자를 `span.ui-btn-wrap` 으로 감싼다. class · aria-* 등 속성은 늘 버튼에 붙는다.
 * - `pressed`(71차 ux-g-common §3-9) — 「도출근거」 · 「상세 표 펼치기」 처럼 눌러 두는 토글. 주면 `aria-pressed` 를 붙이고 ghost 가
 *   활성 면(옅은 파랑 + 파란 선, main.scss)이 된다. 주지 않으면(기본) 토글이 아니라 `aria-pressed` 를 붙이지 않는다.
 *   패널을 열고 닫는 토글이면 호출부가 `aria-expanded` · `aria-controls` 를 함께 단다.
 * - 경과 초는 `showElapsed` 가 꺼졌다 다시 켜지면 0 부터 다시 센다(분당 상한 대기 뒤 재전송 — 대기 시간이 더해져 보이지 않게, CR71-08).
 * - `icon`(73차 ux-m-common §2-2) — 글 왼쪽 16px 아이콘(장식, 글 사이 6px). 처리 중에는 회전 표지가 대신한다. quiet 는 선 · 면이 없어
 *   글자만이면 라벨처럼 읽히므로 아이콘이 필수다(없으면 DEV 경고 — AI 검토 = sparkle · 내보내기 = download · 다시 = refresh).
 * - 89차 C89-18(bl-C7): `iconEnd` — 아이콘을 글 **오른쪽**에 둔다(「근거 보기 ⌄」 · 「열기 ↗」). 호출부가 `flex-direction: row-reverse` 로 뒤집던 것(QuickHireQuestionPanel — 중요도 선언 사다리가 필요했다)을 대신한다.
 *   기본 false 라 쓰지 않는 호출부는 그대로다. 간격 · 처리 중 회전 표지 · `has-icon` 은 왼쪽 아이콘과 같다.
 * - `reasonPlacement="row"`(73차 §2-3) — 히어로 액션 · 섹션 머리 액션처럼 좁은 오른쪽 칸에서 이유가 3~4줄로 꺾이던 자리. 감쌈이
 *   `display: contents`(main.scss `.ui-btn-wrap.is-row`)라 이유 글이 버튼 줄의 형제가 되어 줄 아래 전폭 1줄(≤72ch)로 선다.
 *   자리 규칙: 폼 · 카드 바닥 = below / 히어로 · 섹션 머리 = row / 표 칸 · 칩 = sr.
 * - `sr` 는 **표 칸 · 칩 안에서만**(73차 후반 ux-c C2 — 운영 화면 60곳 중 절반이 카드 머리 · 바닥에 sr 를 써 이유가 눈에 보이지 않았다).
 *   `td · th · .ui-chip · .ui-filter-chips · .ui-segmented · .ui-target-option · .ui-row-actions` 밖의 sr 는 DEV 경고(렌더는 그대로) — below 또는 row 로.
 * - `group`(74차 ux-c C2) — **같은 이유로 잠긴 형제 버튼 묶음**(판정 셋 · 일정 다섯). 버튼마다 같은 이유 글이 서면 묶음이 이유 글로
 *   덮였다. 버튼은 이유를 숨은 설명(aria-describedby)으로만 갖고(title 없음), 보이는 이유는 호출부가 묶음 아래 **한 번**
 *   `<p class="ui-btn-reason ui-btn-group-reason">` 로 쓴다. 규칙: 같은 `blockedReason` 을 나누는 형제 ≥2 면 group + 한 줄.
 *   DEV 경고 — 한 부모 안 보이는 같은 이유 글 2개 이상(→ group), group 인데 가까운 조상(4단계) 안에 같은 글의 `.ui-btn-reason` 줄이 없음.
 * - `hint`(76차 UXS76-01) — 버튼이 **하는 일 · 결과** 설명(「이미 보낸 요청은 끝까지 기다리고 …」). `title` 은 마우스로만 보여 터치 · 키보드
 *   사용자가 볼 수 없었다(R4 가 배지 `StatusBadge hint` 에서 멈췄던 것). 주면 버튼 오른쪽에 ⓘ `InfoTip`(접근 이름 「설명: {버튼 글자}」)을
 *   두고 둘을 `span.ui-btn-hint` 로 묶는다. 버튼은 숨은 글로 `aria-describedby` 를 잇는다. class · aria-* 는 늘 버튼에 붙는다.
 *   `reasonPlacement="sr"`(표 칸 · 칩 · `RowActions`)이면 ⓘ 없이 숨은 설명만. `blockedReason` 이 함께면 이유 줄이 우선이고 ⓘ 는 버튼 옆 그대로.
 *   **덮어쓰거나 확인이 풀리는 결과**(「지금 문구가 바뀝니다」)는 hint 가 아니라 보이는 글 · 확인 창으로 알린다(README 76차).
 *   `title` 이 40자를 넘으면 DEV 경고(StatusBadge 와 같은 상한 · 같은 글은 한 번).
 * - 77차 UXS77-01: ⓘ 를 그리지 않는 자리(`sr`)에서는 `hint` 가 마우스 `title` 을 이어받는다(`title` 속성이 있으면 그쪽 우선, 막힌 이유가 있으면 이유가 우선).
 *   표 칸 설명의 정석은 열 머리 한 번의 ⓘ(`InfoTip`)이므로 `sr` + 40자 넘는 `hint` 는 DEV 경고.
 * - 77차 UXS77-05 · 06: 버튼 마크업은 한 벌이다. 겹은 조건부 껍데기 둘 — 이유 줄이 보이면 `span.ui-btn-wrap`, ⓘ 를 그리면 그 안쪽
 *   `span.ui-btn-hint`(버튼 + ⓘ 한 묶음). 껍데기가 없으면 `Passthrough` 가 버튼만 그린다. 그래서 `reasonPlacement="row"`(래퍼 `display: contents`)에서도
 *   [버튼 ⓘ] 가 줄 흐름의 한 항목으로 붙어 다닌다.
 * - 77차 ux-a C1: `reasonPlacement="page"` — 같은 문장이 화면 머리의 권한 칩 `InfoTip` 에 있을 때. 보이는 이유 줄 없이 숨은 설명 + title 만 단다
 *   (`sr` 와 같은 낭독, 자리 제한 · DEV 경고 없음). 풀 수 있는 막힘(낡음 · 불러오기 실패)에는 쓰지 않는다.
 * - 78차 UXS78-03: `reasonPlacement="auto"`(**기본값**) — 자리를 호출마다 고르지 않고 문맥이 정한다. 명시값은 늘 이긴다. 해석 순서(이유가 있을 때만):
 *   ① 가까운 `REASON_CONTEXT.sameAs()` 가 같은 글(저장 줄 `StickyActionBar` 의 `status`)이면 `group` — 보이는 이유는 그 글 한 번,
 *   ② `provideReasonPage` 로 등록된 화면 머리 문장(읽기 역할 권한 칩)과 같은 글이면 `page`,
 *   ③ 가까운 `REASON_CONTEXT.place`(`StickyActionBar` · `PageHero #actions` · `SectionHead #actions` · `Callout` 동작 = row, `CardFoot` · `AppModal` ·
 *   `SidePanel` = below),
 *   ④ 마운트 · 다시 그릴 때 DOM 이 칩 · 세그먼트 · 대상 목록 · 행 동작 줄 안이거나, **버튼이 유일한 내용인** 표 칸(`td` · `th`)이면 `sr`(같은 틱에 바뀌어 그려지기 전 —
 *   이유 줄만 영향, ⓘ 는 그대로, 껍데기는 그대로 둬 `<button>` 이 다시 만들어지지 않는다). 칸에 목록 · 편집기가 함께 있으면 `below` 를 유지한다(RA78-03),
 *   ⑤ 그 밖 `below`. `inline`(버튼 오른쪽) · 형제 묶음 `group` 은 문맥이 못 만들어 명시값으로 남는다.
 *   DEV `sr` 위치 경고는 명시값일 때만. `dup` · `group` 이유 줄 누락은 **해석된** 자리 기준으로 계속 울린다.
 * - 90차 C90-22(JR90-18): 막힌 버튼을 **누르면**(마우스 · Enter · Space) 지금까지는 아무 일도 없어 화면 읽기 사용자는 이유를 포커스 때 한 번만 들었다 — 이제 `blockedReason` 을 polite live 로 한 번 읽는다
 *   (같은 문장은 1.5초 안 1회, 버튼이 든 `role="dialog"` 안에 구역을 둔다. 처리 중 · 이유 없는 `disabled` 는 알리지 않는다). 시각 변화 0. `blockedFocus`(셀렉터) 를 주면 막은 칸으로 포커스를 옮긴다 —
 *   모달 안이면 그 모달에서 먼저 찾는다(예: 레퍼런스 「미실시로 기록」 → 사유 select). 칸이 없거나 안 보이면 옮기지 않고 알림만. 끝없이 막힌 버튼은 눌러도 아무 데도 안 가므로 이동 칸은 **사용자가 고칠 수 있는 입력**일 때만 준다.
 * - 81차 UXB81-01(리드 M9): `hideWhenPageBlocked`(기본 false) — 화면이 `provideReasonPage` 로 **역할 막힘**(읽기 전용 구성원)을 말하는 동안 **아무것도 그리지 않는다**(리뷰 R-DS-H1 · M2: 이유 자리 `page` 해석 · 이 버튼의 `blockedReason` · 명시 `reasonPlacement` 와 무관한 명시 플래그). 제공자가 없는 화면은 숨기지 않는다(82차 M16).
 *   읽기 전용 화면(조회 · 면접관)이 「꺼진 버튼의 벽」이 되지 않게, 쓰기 전용 버튼(추가 · 다시 추천 · AI 검토 · 행 삭제)에만 건다. 확정 · 저장 · 이동처럼 막힘 설명이
 *   의미 있는 버튼에는 걸지 않는다. 역할이 아닌 화면 머리 문장(게이트 · 선행 단계 — `provideReasonPage(..., { role })` 로 구분)과 풀 수 있는 막힘(낡음 · 불러오기 실패 · 처리 중)에서는 그대로 보인다.
 */
defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    /** 처리 중 */
    loading?: boolean;
    /** 처리 중 문구 */
    loadingLabel?: string;
    /**
     * 버튼 위계(UXE-05): primary(카드당 하나) · ghost(보조) · accent(ghost ui-accent, 다음 할 일 · 표 행 반복) ·
     * quiet(ghost is-quiet, 낮은 보조) · danger(ui-danger) · danger-quiet(ghost ui-danger is-quiet, 행 안 반복 삭제)
     */
    variant?:
      "primary" | "ghost" | "accent" | "quiet" | "danger" | "danger-quiet";
    /** md 42 · sm 36(ui-sm) · cell 36(ui-cell, sm 과 같은 단) · lg 44(ui-lg) — `--ui-btn-h-*` */
    size?: "md" | "sm" | "cell" | "lg";
    /** 버튼 type */
    type?: "button" | "submit";
    /** 잠김(포커스는 유지) */
    disabled?: boolean;
    /** 막힌 이유. 주면 잠그고 이유를 보이는 한 줄로 둔다 */
    blockedReason?: string;
    /**
     * 이유 위치: auto(기본) = 문맥이 정한다(머리 주석), below = 버튼 아래 한 줄, inline = 버튼 오른쪽, row = 버튼 줄 아래 전폭 한 줄(히어로 · 섹션 머리),
     * sr = 화면 읽기만(+ title), group = 같은 이유 형제 묶음(화면 읽기만 — 보이는 한 줄은 호출부가 묶음 아래 `.ui-btn-group-reason`),
     * page = 화면 머리 권한 칩 `InfoTip` 이 같은 문장을 말한다(화면 읽기만 + title, 77차)
     */
    reasonPlacement?: ReasonPlace | "auto";
    /** 처리 중 꼬리표(「(3/10)」) */
    progressText?: string;
    /** 처리 중 경과 초 꼬리표(10초부터) */
    showElapsed?: boolean;
    /** 눌러 두는 토글의 상태(`aria-pressed`). 주지 않으면 토글이 아니다 */
    pressed?: boolean;
    /** 글 왼쪽 아이콘(16, 장식). quiet 는 필수 */
    icon?: UiIconName;
    /** 아이콘을 글 오른쪽에 둔다(「근거 보기 ⌄」). 기본 false — 글 왼쪽 */
    iconEnd?: boolean;
    /** 하는 일 · 결과 설명(40자 넘는 title 대신) — 버튼 오른쪽 ⓘ 설명 판 + aria-describedby */
    hint?: string;
    /** 막힌 버튼을 눌렀을 때 포커스를 옮길 칸(CSS 선택자 — 가까운 모달 안에서 먼저 찾는다). 이유를 풀 수 있는 입력 칸일 때만 */
    blockedFocus?: string;
    /** 화면 제공자(`provideReasonPage`)가 역할 막힘을 알리는 동안 버튼을 그리지 않는다(읽기 모드 — 쓰기 전용 버튼에만). 제공자가 없으면 그리지 않는 일이 없다 */
    hideWhenPageBlocked?: boolean;
  }>(),
  {
    loading: false,
    loadingLabel: "처리 중…",
    variant: "primary",
    size: "md",
    type: "button",
    disabled: false,
    blockedReason: undefined,
    reasonPlacement: "auto",
    progressText: undefined,
    showElapsed: false,
    pressed: undefined,
    icon: undefined,
    iconEnd: false,
    hint: undefined,
    blockedFocus: undefined,
    hideWhenPageBlocked: false,
  },
);

const emit = defineEmits<{
  /** 누름(처리 중 · 잠김 · 막힘일 때는 보내지 않는다) */
  click: [event: MouseEvent];
}>();

defineSlots<{
  /** 평소 문구 */
  default?: () => unknown;
}>();

const VARIANT_CLASSES: Record<NonNullable<typeof props.variant>, string[]> = {
  primary: ["primary"],
  ghost: ["ghost"],
  accent: ["ghost", "ui-accent"],
  quiet: ["ghost", "is-quiet"],
  danger: ["ui-danger"],
  "danger-quiet": ["ghost", "ui-danger", "is-quiet"],
};

const SIZE_CLASSES: Record<NonNullable<typeof props.size>, string[]> = {
  md: [],
  sm: ["ui-sm"],
  cell: ["ui-cell"],
  lg: ["ui-lg"],
};

/**
 * 껍데기가 필요 없는 갈래 — 첫 자식(버튼 · 안쪽 껍데기)만 그린다. 속성은 받지 않는다(버튼이 `attrs` 를 직접 받는다).
 * 자식 배열 전체가 아니라 **하나만** 돌려주는 까닭: 배열이면 컴포넌트 루트가 Fragment 가 되어 호출부의 `ref.$el` 이 버튼(또는 껍데기)이 아니라
 * 빈 앵커 노드가 된다(`InterviewPrepTraining` · `ApplicantsBulkResumeModal` 이 `$el` 로 포커스를 옮긴다). 이 갈래의 둘째 자식은 `v-if` 주석뿐이다.
 */
const Passthrough: FunctionalComponent = (_props, { slots }) =>
  slots.default?.()[0];
Passthrough.inheritAttrs = false;

/** 경과 초 꼬리표를 보이기 시작하는 초 */
const ELAPSED_FROM_SECONDS = 10;

const attrs = useAttrs();
const button = useTemplateRef<HTMLButtonElement>("button");
const reasonId = useId();
const hintId = useId();
const lockedWidth = ref<number>();
const elapsedSeconds = ref(0);
let elapsedTimer: ReturnType<typeof setInterval> | undefined;

const blocked = computed(
  () => props.loading || props.disabled || !!props.blockedReason,
);
/** 처리 중에는 이유 대신 처리 중 문구가 상태를 전한다 */
const reasonShown = computed(() => !props.loading && !!props.blockedReason);

const reasonContext = inject(REASON_CONTEXT, undefined);
const pageReason = inject(REASON_PAGE, undefined);
const pageBlockedByRole = usePageBlockedByRole();
/** `auto` 이고 문맥이 자리를 주지 않을 때, 마운트 뒤 DOM 이 표 칸 · 칩 안임을 확인했다 */
const insideSrPlace = ref(false);

/** 이유 글의 해석된 자리(`auto` 는 문맥 순서대로 — 머리 주석) */
const place = computed<ReasonPlace>(() => {
  if (props.reasonPlacement !== "auto") return props.reasonPlacement;
  const reason = props.blockedReason;
  if (reason) {
    if (reasonContext?.sameAs?.() === reason) return "group";
    if (pageReason?.() === reason) return "page";
  }
  if (reasonContext?.place) return reasonContext.place;
  return insideSrPlace.value ? "sr" : "below";
});
// 93차 C93-13: 수집하는 문맥(`AppModal` 바닥 줄) 안에서 `auto` 로 `group` 이 된 버튼은 이유 글을 문맥에 알려 줄 위 한 줄로 말하게 한다. 호출부는 아무것도 하지 않는다.
// 첫 그림 중에 알리면 부모가 이미 그린 뒤라 갱신이 버려지므로 마운트 뒤(post)에 알린다.
const collectedReason = computed(() =>
  reasonContext?.collect &&
  props.reasonPlacement === "auto" &&
  place.value === "group" &&
  reasonShown.value
    ? props.blockedReason
    : undefined,
);

/**
 * 읽기 모드 — **역할 막힘일 때만** 버튼 자체를 그리지 않는다(R-DS-H1 · M2 · review-fe-a M1). 게이트 · 불러오기 실패 · 처리 중 같은 풀 수 있는 막힘은 꺼진 버튼 + 이유가 보여야 한다.
 * - 화면이 `provideReasonPage` 로 역할 막힘을 명시하면(`REASON_PAGE_ROLE`) 그 플래그만 본다 — 이유 자리 해석(`place`) · 이 버튼의 `blockedReason` · 명시 `reasonPlacement` 와 무관하다.
 * - 제공자가 없는 화면은 숨기지 않는다(82차 M16 — 옛 「이유가 `page` 자리로 풀린 버튼」 갈래 삭제, 쓰는 곳 0 이었다).
 */
const hiddenByPage = computed(() => {
  return props.hideWhenPageBlocked && pageBlockedByRole.value;
});
const reasonVisible = computed(
  () =>
    reasonShown.value &&
    place.value !== "sr" &&
    place.value !== "group" &&
    place.value !== "page",
);
/**
 * 껍데기(`span.ui-btn-wrap`)를 그린다 — 보이는 이유 줄이 있을 때, 그리고 **DOM 판정으로 `sr` 가 된 `auto`** 일 때(첫 그림에서 이미 껍데기가 있었으므로 판정 뒤에도
 * 그대로 둬서 `<button>` 요소가 새로 만들어지지 않게 한다 — 78차 RA78-06). 껍데기만 있고 이유 줄이 없는 모양은 `sr` 와 같은 낭독이다.
 */
const shellShown = computed(
  () =>
    reasonVisible.value ||
    (reasonShown.value &&
      props.reasonPlacement === "auto" &&
      insideSrPlace.value),
);
/** ⓘ 를 그린다(hint 가 있고 표 칸 · 칩 자리가 아님 — 그 자리는 숨은 설명만). 이유 줄이 아니라 명시한 자리 · 문맥만 본다(DOM 으로 알아낸 `sr` 는 ⓘ 를 지우지 않는다) */
const hintTipShown = computed(() => {
  if (!props.hint) return false;
  const hintPlace =
    props.reasonPlacement === "auto"
      ? reasonContext?.place
      : props.reasonPlacement;
  return hintPlace !== "sr";
});
/** ⓘ 접근 이름 대상 — 버튼 글자(렌더 뒤 DOM 에서 읽는다). 읽기 전에는 「버튼」 */
const hintLabel = ref("버튼");
// 호출부가 넘긴 설명 · 툴팁을 지우지 않는다(뒤에 둔 바인딩이 v-bind="attrs" 의 같은 키를 덮으므로 여기서 합친다).
const describedBy = computed(
  () =>
    [
      attrs["aria-describedby"],
      reasonShown.value ? reasonId : undefined,
      props.hint ? hintId : undefined,
    ]
      .filter((id): id is string => typeof id === "string" && id !== "")
      .join(" ") || undefined,
);
// 숨은 글로만 이유를 말하는 자리(sr · page)는 이유를 title 에도 둔다. group 은 묶음 아래 보이는 줄이 말하므로 겹쳐 두지 않는다.
// 보이는 이유 줄이 있으면 title 은 호출부 것만. ⓘ 를 그리지 않는 자리(sr)면 hint 가 title 을 이어받는다(UXS77-01 — 76차가 title 을 hint 로
// 옮기며 표 칸에서 마우스 설명이 사라졌다). `title` 속성이 있으면 그쪽이 우선.
const titleText = computed(() => {
  if (reasonShown.value && (place.value === "sr" || place.value === "page"))
    return props.blockedReason;
  if (typeof attrs.title === "string") return attrs.title;
  return props.hint && !hintTipShown.value ? props.hint : undefined;
});
const classes = computed(() => [
  ...VARIANT_CLASSES[props.variant],
  ...SIZE_CLASSES[props.size],
  // 처리 중에는 아이콘 대신 회전 표지(자기 오른쪽 여백 6)가 서므로 간격을 더하지 않는다.
  ...(props.icon && !props.loading ? ["has-icon"] : []),
]);

if (import.meta.env.DEV)
  watch(
    () => [props.variant, props.icon] as const,
    ([variant, icon]) => {
      if (variant === "quiet" && !icon)
        console.warn(
          "[LoadingButton] quiet 는 아이콘이 필수입니다(73차) — icon 을 주세요(AI 검토 = sparkle · 내보내기 = download)",
        );
    },
    { immediate: true },
  );
/** `title` 상한(글자 수) — 넘으면 `hint` 로(StatusBadge 와 같다) */
const TITLE_MAX = 40;

if (import.meta.env.DEV)
  onMounted(() => {
    const title = attrs.title;
    if (typeof title !== "string" || title.length <= TITLE_MAX) return;
    if (warnedTitles.has(title)) return;
    warnedTitles.add(title);
    console.warn(
      `[LoadingButton] title ${title.length}자(≤${TITLE_MAX}) — 버튼이 하는 일 · 결과 설명은 hint 로(터치 · 키보드로 열리는 InfoTip), 덮어쓰는 결과는 보이는 글 · 확인 창으로 (label: ${button.value?.textContent?.trim() ?? "-"})`,
    );
  });

/** DEV: 표 한 열에 같은 글의 버튼 3개 이상(`[ui-row-repeat]`, 91차 UXS91-15 — 규칙은 `rowRepeat.ts`) */
if (import.meta.env.DEV)
  onMounted(() => {
    scheduleRowRepeatCheck(button.value);
  });

/** 이유가 화면 머리 권한 칩(`page`) · 저장 줄 상태(`sameAs`)와 같은 글이면 `sr` 자리 · 설명 길이 DEV 검사에서 뺀다(RG81-08 — 80차 「같은 이유 한 번」 규칙이 만든 정상 모양) */
function reasonToldElsewhere(): boolean {
  const reason = props.blockedReason;
  return (
    !!reason &&
    (pageReason?.() === reason || reasonContext?.sameAs?.() === reason)
  );
}

/** DEV: 표 칸(`sr`)의 긴 hint — 설명이 마우스 title 뿐이다. 정석은 열 머리 한 번의 ⓘ(`InfoTip`) 또는 행 안내 */
if (import.meta.env.DEV)
  onMounted(() => {
    const hint = props.hint;
    if (!hint || props.reasonPlacement !== "sr" || hint.length <= TITLE_MAX)
      return;
    if (reasonToldElsewhere()) return;
    if (warnedTitles.has(hint)) return;
    warnedTitles.add(hint);
    console.warn(
      `[LoadingButton] hint ${hint.length}자 + reasonPlacement="sr" — 표 칸에서는 설명이 마우스 title 뿐입니다. 열 머리 ⓘ(InfoTip) 또는 행 안내로 (label: ${button.value?.textContent?.trim() ?? "-"})`,
    );
  });

/** ⓘ 접근 이름 「설명: {버튼 글자}」 — 버튼 글자는 슬롯이라 렌더 뒤에 읽는다(aria-label 이 있으면 그것) */
function readHintLabel(): void {
  if (!props.hint) return;
  const own = attrs["aria-label"];
  // 숨은 설명(이유 · hint — `hidden` span)은 버튼 이름이 아니므로 뺀다.
  // 주석 노드(`<!-- v-if -->` 자리 표지 · 템플릿 주석)의 글은 `textContent` 에 들어오므로 거른다(JR80-12 — 「설명: v-if」 가 접근 이름에 섞였다).
  const visible = [...(button.value?.childNodes ?? [])]
    .filter(
      (node) =>
        node.nodeType !== Node.COMMENT_NODE &&
        !(node instanceof HTMLElement && node.hidden),
    )
    .map((node) => node.textContent ?? "")
    .join("");
  const text =
    typeof own === "string" && own !== ""
      ? own
      : visible.replace(/\s+/g, " ").trim();
  if (text && !props.loading) hintLabel.value = text;
}

onMounted(readHintLabel);
// 버튼 글자(슬롯)가 바뀌면 다시 읽는다 — 같은 값이면 다시 그리지 않는다.
onUpdated(readHintLabel);
watch(() => [props.hint, props.loading] as const, readHintLabel, {
  flush: "post",
});

/** `sr` 를 둘 수 있는 자리(표 칸 · 칩 · 세그먼트 · 대상 목록 칸 · 행 동작 줄 — `RowActions` 는 이유를 sr 로 고정한다) */
const SR_PLACES =
  "td, th, .ui-chip, .ui-filter-chips, .ui-segmented, .ui-target-option, .ui-row-actions";

/**
 * 이 버튼이 `sr` 자리(표 칸 · 칩 · 세그먼트 · 대상 목록 · 행 동작 줄)의 **동작**인가. 칩류는 안이면 늘 참이고, `td` · `th` 는 이 버튼(과 `ui-btn-wrap` · `ui-btn-hint`
 * 껍데기)이 **칸의 유일한 자식**일 때만 참이다 — 칸 안에 목록 · 편집기 같은 블록 내용이 함께 있으면(`행동지표 추가` 가 든 AI 리터러시 표 칸, RA78-03) 보이는 이유가
 * 숨으면 안 되므로 `below` 를 유지한다.
 */
function isCellAction(el: HTMLElement): boolean {
  const cell = el.closest(SR_PLACES);
  if (!cell) return false;
  if (!cell.matches("td, th")) return true;
  let node: HTMLElement | null = el;
  while (node && node.parentElement !== cell) {
    const parent: HTMLElement | null = node.parentElement;
    if (!parent?.matches(".ui-btn-wrap, .ui-btn-hint")) return false;
    node = parent;
  }
  return !!node && cell.children.length === 1;
}

// 78차: `auto` 인데 문맥이 자리를 주지 않으면 DOM 으로 표 칸 · 칩 안인지 본다(마운트 + 다시 그릴 때마다 — 버튼이 다른 칸으로 옮겨지는 경우도). 값이 같으면 다시 그리지 않는다.
function judgeSrPlace(): void {
  if (props.reasonPlacement !== "auto" || reasonContext?.place) return;
  const el = button.value;
  insideSrPlace.value = !!el && isCellAction(el);
}

onMounted(judgeSrPlace);
onUpdated(judgeSrPlace);

if (import.meta.env.DEV)
  onMounted(() => {
    if (
      props.reasonPlacement === "sr" &&
      !button.value?.closest(SR_PLACES) &&
      !reasonToldElsewhere()
    )
      console.warn(
        `[LoadingButton] reasonPlacement="sr" 는 표 칸 · 칩 안에서만 — 카드 머리 · 바닥 · 폼은 below 또는 row 로 (label: ${button.value?.textContent?.trim() ?? "-"})`,
      );
  });

/** 묶음 이유 줄을 찾는 조상 깊이 — 버튼 → 버튼 줄 → 카드 바닥 · 칸 → 그 위 묶음 */
const GROUP_REASON_DEPTH = 4;

/** DEV: 같은 이유 글 반복 · group 의 이유 줄 누락 — 형제 · 호출부 마크업은 렌더 뒤 DOM 으로만 안다 */
function warnReasonGroup(): void {
  const el = button.value;
  const reason = props.blockedReason;
  if (!el || !reason || props.loading) return;
  if (place.value === "group") {
    // 수집하는 문맥(`AppModal` 바닥 줄)이 이유 줄을 대신 그린다 — 마운트 직후엔 아직 없으니 찾지 않는다.
    if (reasonContext?.collect) return;
    let node: HTMLElement | null = el.parentElement;
    for (let depth = 0; node && depth < GROUP_REASON_DEPTH; depth += 1) {
      const lines = node.querySelectorAll(".ui-btn-reason");
      if ([...lines].some((line) => line.textContent?.trim() === reason))
        return;
      node = node.parentElement;
    }
    const key = `missing:${reason}`;
    if (warnedReasons.has(key)) return;
    warnedReasons.add(key);
    console.warn(
      `[LoadingButton] reasonPlacement="group" 인데 묶음 아래 이유 줄이 없습니다 — <p class="ui-btn-reason ui-btn-group-reason">${reason}</p>`,
    );
    return;
  }
  if (!reasonVisible.value) return;
  const row = el.closest(".ui-btn-wrap")?.parentElement;
  if (!row) return;
  const same = [
    ...row.querySelectorAll(":scope > .ui-btn-wrap > .ui-btn-reason"),
  ].filter((line) => line.textContent?.trim() === reason).length;
  const key = `dup:${reason}`;
  if (same < 2 || warnedReasons.has(key)) return;
  warnedReasons.add(key);
  console.warn(
    `[LoadingButton] 같은 이유 글이 한 줄에 ${same}번 — 형제 버튼은 reasonPlacement="group" + 묶음 아래 한 줄로 (reason: ${reason})`,
  );
}

if (import.meta.env.DEV) {
  onMounted(warnReasonGroup);
  watch(() => [props.blockedReason, props.loading] as const, warnReasonGroup, {
    flush: "post",
  });
}

const loadingText = computed(() => {
  let text = props.loadingLabel;
  if (props.progressText) text += ` ${props.progressText}`;
  if (props.showElapsed && elapsedSeconds.value >= ELAPSED_FROM_SECONDS) {
    text += ` · ${elapsedSeconds.value}초`;
  }
  return text;
});

/**
 * `aria-pressed` 값. 뒤에 둔 바인딩이 `v-bind="attrs"` 의 같은 키를 덮으므로, `pressed` 를 주지 않은 호출부가 속성으로 단
 * `aria-pressed`(비전 후보 표 · 후보 카드)를 그대로 이어 준다. attrs 는 반응형이 아니라 computed 대신 렌더마다 읽는다.
 */
function pressedAttr(): "true" | "false" | "mixed" | undefined {
  if (props.pressed !== undefined) return props.pressed ? "true" : "false";
  const attr = attrs["aria-pressed"];
  if (attr === "mixed") return "mixed";
  if (attr === undefined || attr === null) return undefined;
  return attr === true || attr === "true" ? "true" : "false";
}

function stopElapsed() {
  if (elapsedTimer !== undefined) clearInterval(elapsedTimer);
  elapsedTimer = undefined;
  elapsedSeconds.value = 0;
}

/** 처리 중이고 경과를 보일 때만 0 부터 센다 */
function restartElapsed() {
  stopElapsed();
  if (props.loading && props.showElapsed) {
    elapsedTimer = setInterval(() => {
      elapsedSeconds.value += 1;
    }, 1000);
  }
}

// 기본 flush("pre")라 문구가 바뀌기 전 DOM 의 폭을 읽는다.
watch(
  () => props.loading,
  (loading) => {
    lockedWidth.value = loading ? button.value?.offsetWidth : undefined;
    restartElapsed();
  },
  { immediate: true },
);

// 처리 중에 경과 표시를 껐다 켜면(분당 상한 대기 → 재전송) 대기 시간을 빼고 다시 센다.
watch(() => props.showElapsed, restartElapsed);

// 이유 글자나 ⓘ 가 생기거나 사라지면 껍데기가 바뀌어 버튼이 다시 그려진다. 포커스가 있었으면 새 버튼으로 되돌린다.
watch([shellShown, hintTipShown], async () => {
  const hadFocus = !!button.value && document.activeElement === button.value;
  if (!hadFocus) return;
  await nextTick();
  button.value?.focus();
});

onBeforeUnmount(stopElapsed);
if (reasonContext?.collect) {
  const collect = reasonContext.collect;
  onMounted(() => {
    collect(reasonId, collectedReason.value);
  });
  watch(
    collectedReason,
    (text) => {
      collect(reasonId, text);
    },
    { flush: "post" },
  );
  onBeforeUnmount(() => {
    collect(reasonId, undefined);
  });
}

/** 막은 칸으로 포커스(`blockedFocus`) — 가까운 모달 안 → 문서 순. 숨겨진 칸 · 없는 칸은 건너뛴다 */
function focusBlockedTarget(): void {
  const selector = props.blockedFocus;
  if (!selector) return;
  const scope = button.value?.closest<HTMLElement>('[role="dialog"]');
  const target = [
    ...(scope?.querySelectorAll<HTMLElement>(selector) ?? []),
    ...document.querySelectorAll<HTMLElement>(selector),
  ].find((el) => el.checkVisibility({ visibilityProperty: true }));
  if (!target) return;
  if (!target.matches("input, select, textarea, button, a[href], [tabindex]"))
    target.tabIndex = -1;
  target.focus();
}

function onClick(event: MouseEvent) {
  if (blocked.value) {
    event.preventDefault();
    if (!props.loading) {
      if (props.blockedReason)
        announceReason(props.blockedReason, button.value);
      focusBlockedTarget();
    }
    return;
  }
  emit("click", event);
}
</script>

<template>
  <component
    :is="shellShown ? 'span' : Passthrough"
    v-if="!hiddenByPage"
    :class="
      shellShown
        ? {
            'ui-btn-wrap': true,
            'is-inline': place === 'inline',
            'is-row': place === 'row',
          }
        : undefined
    "
  >
    <component
      :is="hintTipShown ? 'span' : Passthrough"
      :class="hintTipShown ? 'ui-btn-hint' : undefined"
    >
      <button
        v-bind="attrs"
        ref="button"
        :type="type"
        class="btn"
        :class="classes"
        :aria-disabled="blocked ? 'true' : undefined"
        :aria-busy="loading ? 'true' : undefined"
        :aria-pressed="pressedAttr()"
        :aria-describedby="describedBy"
        :title="titleText"
        :style="lockedWidth ? { minWidth: `${lockedWidth}px` } : undefined"
        @click="onClick"
      >
        <template v-if="loading">
          <span class="ui-btn-spinner" aria-hidden="true" />{{ loadingText }}
        </template>
        <template v-else>
          <UiIcon
            v-if="icon && !iconEnd"
            class="ui-btn-icon"
            :name="icon"
            :size="16"
          />
          <slot />
          <UiIcon
            v-if="icon && iconEnd"
            class="ui-btn-icon"
            :name="icon"
            :size="16"
          />
        </template>
        <!-- 숨은 설명: hidden 이라 버튼 이름에서는 빠지고 aria-describedby 로만 읽힌다(이유 줄이 보이면 그 줄이 id 를 갖는다) -->
        <span v-if="reasonShown && !reasonVisible" :id="reasonId" hidden>{{
          blockedReason
        }}</span>
        <span v-if="hint" :id="hintId" hidden>{{ hint }}</span>
      </button>
      <InfoTip
        v-if="hintTipShown"
        class="ui-btn-hint-tip"
        :label="hintLabel"
        :text="hint"
      />
    </component>
    <span v-if="reasonVisible" :id="reasonId" class="ui-btn-reason">{{
      blockedReason
    }}</span>
  </component>
</template>

<style scoped>
/* 아이콘 버튼 줄 — 아이콘 16 · 글 사이 6(ux-m-common §2-2). 프로토타입 `.btn` 은 inline-block 이라 여기서 가로 정렬을 준다. */
.btn.has-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.ui-btn-icon {
  flex: none;
}

/* 버튼 + ⓘ 묶음(`.ui-btn-hint`)의 모양은 전역 `.ui-btn-wrap` 과 같은 층(main.scss 「76차」 · 「77차」 — `#app#app`)에 둔다. */
</style>
